import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getToken, setToken, clearToken, authHeader } from "@/lib/adminAuth";
import { circuloDe } from "@/lib/directoryConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  {
    key: "hero",
    label: "Hero — fondo principal",
    hint: "Foto ambiente del evento (formato horizontal, ideal 1920 × 1080)",
    ratio: "16/9",
  },
  {
    key: "community",
    label: "Comunidad — foto de grupo",
    hint: "Foto grande horizontal de participantes (ideal 1800 × 1200)",
    ratio: "3/2",
  },
  {
    key: "venue",
    label: "Sede — Radisson Collection Hotel Sevilla",
    hint: "Foto vertical o cuadrada del hotel / sala de reuniones (ideal 1200 × 1500)",
    ratio: "4/5",
  },
  {
    key: "founder-esther",
    label: "Esther Parejo",
    hint: "Retrato editorial vertical (ideal 800 × 1000)",
    ratio: "4/5",
  },
  {
    key: "speaker-lula",
    label: "Lula Ballarino",
    hint: "Retrato editorial vertical (ideal 800 × 1000)",
    ratio: "3/4",
  },
  {
    key: "speaker-francisca",
    label: "Francisca Molina",
    hint: "Retrato editorial vertical (ideal 800 × 1000)",
    ratio: "3/4",
  },
  {
    key: "speaker-karla",
    label: "Karla Ramírez Rey",
    hint: "Retrato editorial vertical (ideal 800 × 1000)",
    ratio: "3/4",
  },
  {
    key: "speaker-veronica",
    label: "Verónica Chumillas",
    hint: "Retrato editorial vertical (ideal 800 × 1000)",
    ratio: "3/4",
  },
  {
    key: "speaker-ana",
    label: "Ana del Pino",
    hint: "Retrato editorial vertical (ideal 800 × 1000)",
    ratio: "3/4",
  },
  {
    key: "gallery",
    label: "Galería (comunidad, networking, ediciones anteriores)",
    hint: "Puedes subir varias — se muestran como galería opcional",
    ratio: "3/4",
    multi: true,
  },
];

// -------- LOGIN --------
const LoginView = ({ onLogged }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setToken(data.token);
      toast.success("Bienvenida");
      onLogged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Credenciales incorrectas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4EFE7] flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-[#FAF8F3] border border-[#3F403A]/12 p-8 md:p-10">
        <p className="overline text-[#63846F]">Panel privado</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#3F403A] mt-3">
          Gestor de <span className="font-quiche text-[#63846F]"><strong className="quiche-bold font-extrabold">imágenes</strong></span>
        </h1>
        <p className="mt-2 font-sans text-sm text-[#3F403A]/70">
          Accede con tus credenciales de administradora.
        </p>

        <form className="mt-8 space-y-6" onSubmit={onSubmit} data-testid="admin-login-form" noValidate>
          <label className="block">
            <span className="overline text-[#3F403A]/60 block mb-2">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="esther@lideraconvozpropia.com"
              className="w-full bg-transparent border-b border-[#3F403A]/25 focus:border-[#63846F] focus:outline-none py-3 px-1 font-sans text-base text-[#3F403A] placeholder:text-[#3F403A]/40 transition-colors"
              data-testid="admin-login-email"
            />
          </label>
          <label className="block">
            <span className="overline text-[#3F403A]/60 block mb-2">Contraseña</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              className="w-full bg-transparent border-b border-[#3F403A]/25 focus:border-[#63846F] focus:outline-none py-3 px-1 font-sans text-base text-[#3F403A] placeholder:text-[#3F403A]/40 transition-colors"
              data-testid="admin-login-password"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-3 rounded-none border border-[#63846F] bg-[#63846F] text-[#FAF8F3] hover:bg-[#4E6D5A] hover:border-[#4E6D5A] transition-colors px-8 py-4 uppercase tracking-[0.22em] text-xs font-medium disabled:opacity-60"
          >
            {loading ? "Accediendo…" : "Acceder"}
          </button>
        </form>
      </div>
    </div>
  );
};

// -------- SLOT CARD --------
const SlotCard = ({ slot, images, onUpload, onDelete }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Archivo demasiado grande (máx 6 MB)");
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(slot.key, file);
      setFile(null);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const current = slot.multi ? images : images.slice(0, 1);

  return (
    <div className="bg-[#FAF8F3] border border-[#3F403A]/12 p-6 md:p-8" data-testid={`slot-card-${slot.key}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="overline text-[#63846F]">{slot.key}</p>
          <h3 className="font-serif text-2xl text-[#3F403A] mt-1">{slot.label}</h3>
          <p className="mt-2 text-sm font-sans text-[#3F403A]/70">{slot.hint}</p>
        </div>
      </div>

      {/* Current images */}
      {current.length > 0 && (
        <div className={`mt-6 grid gap-4 ${slot.multi ? "grid-cols-2 md:grid-cols-3" : "grid-cols-1"}`}>
          {current.map((img) => (
            <div key={img.id} className="relative group border border-[#3F403A]/10" data-testid={`slot-image-${img.id}`}>
              <div style={{ aspectRatio: slot.ratio }} className="overflow-hidden bg-[#F4EFE7]">
                <img src={img.url} alt={img.filename} className="w-full h-full object-cover" />
              </div>
              <div className="flex items-center justify-between px-3 py-2 bg-[#F4EFE7] text-xs">
                <span className="truncate text-[#3F403A]/70">{img.width}×{img.height} · {Math.round(img.size/1024)} KB</span>
                <button
                  type="button"
                  onClick={() => onDelete(img.id)}
                  className="overline text-[#B77A4B] hover:text-[#a0673d]"
                  data-testid={`delete-image-${img.id}`}
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload form */}
      <div className="mt-6 pt-6 border-t border-[#3F403A]/10">
        {preview && (
          <div className="mb-4 border border-[#3F403A]/10" style={{ aspectRatio: slot.ratio, maxWidth: 240 }}>
            <img src={preview} alt="preview" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <label className="inline-flex items-center gap-3 border border-[#3F403A]/25 bg-transparent px-4 py-3 cursor-pointer hover:border-[#63846F] transition-colors">
            <span className="overline text-[#3F403A]/70">Elegir archivo</span>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFile}
              className="hidden"
              data-testid={`file-input-${slot.key}`}
            />
          </label>
          <span className="text-xs text-[#3F403A]/60 font-sans truncate">{file?.name || "Ningún archivo seleccionado"}</span>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || uploading}
            className="ml-auto inline-flex items-center gap-2 border border-[#63846F] bg-[#63846F] text-[#FAF8F3] hover:bg-[#4E6D5A] transition-colors px-5 py-3 uppercase tracking-[0.22em] text-[11px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`upload-${slot.key}`}
          >
            {uploading ? "Subiendo…" : slot.multi ? "Añadir foto" : "Sustituir foto"}
          </button>
        </div>
        <p className="mt-3 text-[11px] text-[#3F403A]/50">
          JPG, PNG o WEBP · máx 6 MB · se optimiza automáticamente si mide más de 2000 px.
        </p>
      </div>
    </div>
  );
};

// -------- ATTENDEES PANEL (key recovery) --------
const AttendeesPanel = () => {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/attendees`, { headers: authHeader() });
      setAttendees(data);
    } catch {
      toast.error("No se han podido cargar las asistentes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const copyLink = async (a) => {
    const url = `${window.location.origin}${a.access_url}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Enlace de ${a.nombre} copiado — envíaselo por WhatsApp o email`);
    } catch {
      toast.error("No se ha podido copiar el enlace");
    }
  };

  const removeAttendee = async (a) => {
    // Two-step confirm: first click arms the button, second executes.
    if (confirmId !== a.id) {
      setConfirmId(a.id);
      setTimeout(() => setConfirmId((c) => (c === a.id ? null : c)), 3500);
      return;
    }
    try {
      await axios.delete(`${API}/admin/attendees/${a.id}`, { headers: authHeader() });
      toast.success(`Ficha de ${a.nombre} eliminada del directorio`);
      setConfirmId(null);
      load();
    } catch {
      toast.error("No se ha podido eliminar la ficha");
    }
  };

  return (
    <section className="mb-12 bg-[#FAF8F3] border border-[#3F403A]/12 p-6 md:p-8" data-testid="attendees-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <p className="overline text-[#63846F]">Directorio privado</p>
          <h2 className="font-serif text-2xl md:text-3xl text-[#3F403A] mt-1" data-testid="attendees-count">
            Asistentes registradas <span className="italic text-[#63846F]">({attendees.length})</span>
          </h2>
          <p className="mt-2 text-sm font-sans text-[#3F403A]/70 leading-relaxed">
            Si alguien no guardó su llave, copia su enlace de acceso y envíaselo
            por WhatsApp o email. Ese mismo enlace abre el directorio y le
            permite editar su ficha.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="overline text-[#3F403A]/70 hover:text-[#63846F] transition-colors shrink-0"
          data-testid="attendees-refresh"
        >
          Actualizar
        </button>
      </div>

      {loading ? (
        <p className="mt-6 font-sans text-sm text-[#3F403A]/60">Cargando asistentes…</p>
      ) : attendees.length === 0 ? (
        <p className="mt-6 font-sans text-sm text-[#3F403A]/60" data-testid="attendees-empty">
          Todavía no hay asistentes registradas. Comparte el enlace{" "}
          <span className="text-[#63846F]">{window.location.origin}/registro</span>{" "}
          con las inscritas.
        </p>
      ) : (
        <ul className="mt-6 divide-y divide-[#3F403A]/10">
          {attendees.map((a) => (
            <li key={a.id} className="py-4 flex items-center gap-4 flex-wrap" data-testid={`attendee-row-${a.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg text-[#3F403A] leading-tight">{a.nombre}</p>
                <p className="mt-1 text-xs font-sans text-[#3F403A]/60 truncate">
                  {a.email} · {a.dedicacion}
                  {a.punto_partida ? ` · Círculo ${circuloDe(a.punto_partida)}` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => copyLink(a)}
                className="inline-flex items-center gap-2 border border-[#63846F] text-[#63846F] hover:bg-[#63846F] hover:text-[#FAF8F3] transition-colors px-4 py-2 uppercase tracking-[0.18em] text-[11px] font-medium shrink-0"
                data-testid={`copy-access-${a.id}`}
              >
                Copiar enlace de acceso
              </button>
              <button
                type="button"
                onClick={() => removeAttendee(a)}
                className={`inline-flex items-center gap-2 border transition-colors px-4 py-2 uppercase tracking-[0.18em] text-[11px] font-medium shrink-0 ${
                  confirmId === a.id
                    ? "border-[#B77A4B] bg-[#B77A4B] text-[#FAF8F3]"
                    : "border-[#3F403A]/25 text-[#3F403A]/60 hover:border-[#B77A4B] hover:text-[#B77A4B]"
                }`}
                data-testid={`delete-attendee-${a.id}`}
              >
                {confirmId === a.id ? "Confirmar eliminación" : "Eliminar"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// -------- DASHBOARD --------
const Dashboard = ({ onLogout }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadImages = async () => {
    try {
      const { data } = await axios.get(`${API}/images`);
      // Attach full URL
      const mapped = data.map((img) => ({
        ...img,
        url: `${process.env.REACT_APP_BACKEND_URL}${img.url}`,
      }));
      setImages(mapped);
    } catch (err) {
      toast.error("No se han podido cargar las imágenes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, []);

  const upload = async (slotKey, file) => {
    const fd = new FormData();
    fd.append("slot", slotKey);
    fd.append("file", file);
    try {
      await axios.post(`${API}/admin/images`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      toast.success("Imagen subida");
      await loadImages();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesión expirada. Vuelve a iniciar sesión.");
        clearToken();
        onLogout();
        return;
      }
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al subir la imagen");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta imagen?")) return;
    try {
      await axios.delete(`${API}/admin/images/${id}`, { headers: authHeader() });
      toast.success("Imagen eliminada");
      await loadImages();
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        onLogout();
        return;
      }
      toast.error("No se ha podido eliminar la imagen");
    }
  };

  const bySlot = useMemo(() => {
    const map = {};
    for (const s of SLOTS) map[s.key] = [];
    for (const img of images) {
      if (map[img.slot]) map[img.slot].push(img);
    }
    return map;
  }, [images]);

  return (
    <div className="min-h-screen bg-[#F4EFE7]">
      <header className="border-b border-[#3F403A]/12 bg-[#FAF8F3]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div>
            <p className="overline text-[#63846F]">Panel privado</p>
            <p className="font-serif text-xl text-[#3F403A]">Gestor de imágenes</p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" className="overline text-[#3F403A]/70 hover:text-[#63846F]" data-testid="admin-view-landing">
              Ver landing ↗
            </a>
            <button
              type="button"
              onClick={onLogout}
              className="border border-[#3F403A]/30 text-[#3F403A] hover:border-[#63846F] hover:text-[#63846F] px-4 py-2 overline transition-colors"
              data-testid="admin-logout"
            >
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="max-w-2xl mb-10">
          <h1 className="font-serif text-3xl md:text-4xl text-[#3F403A]">
            Sube y gestiona las fotografías reales del evento.
          </h1>
          <p className="mt-4 font-sans text-sm md:text-base text-[#3F403A]/75 leading-relaxed">
            Cada foto se muestra en su sitio correspondiente de la landing en cuanto se sube. Puedes
            reemplazarla cuantas veces quieras; las anteriores se eliminan automáticamente.
          </p>
        </div>

        <AttendeesPanel />

        {loading ? (
          <p className="font-sans text-sm text-[#3F403A]/60">Cargando imágenes…</p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:gap-8">
            {SLOTS.map((s) => (
              <SlotCard
                key={s.key}
                slot={s}
                images={bySlot[s.key] || []}
                onUpload={upload}
                onDelete={remove}
              />
            ))}
          </div>
        )}
      </main>    </div>
  );
};

// -------- ROOT --------
export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [checking, setChecking] = useState(!!getToken());

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      const token = getToken();
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) {
          setAuthed(true);
        }
      } catch {
        clearToken();
        if (!cancelled) setAuthed(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    verify();
    return () => {
      cancelled = true;
    };
  }, []);

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#F4EFE7] flex items-center justify-center">
        <p className="font-sans text-sm text-[#3F403A]/60">Comprobando sesión…</p>
      </div>
    );
  }

  return authed ? (
    <Dashboard onLogout={logout} />
  ) : (
    <LoginView onLogged={() => setAuthed(true)} />
  );
}
